// src/audio/audio-client.ts
import { getWsUrl } from "../lib/ws";

// --- Transcript subscription API (backward-compatible) ---
type TranscriptEvent = { type: 'partial' | 'final', text: string };

// Accept EITHER a single function OR an object with onTranscript/onStreamEnd
type TranscriptHandler =
  | ((payload: TranscriptEvent) => void)
  | {
      onTranscript: (payload: TranscriptEvent) => void;
      onStreamEnd?: () => void;
    };

let _transcriptHandler: TranscriptHandler | null = null;

export function setTranscriptHandler(handler: TranscriptHandler | null) {
  _transcriptHandler = handler;
}

function isObjectHandler(
  h: TranscriptHandler
): h is { onTranscript: (p: TranscriptEvent) => void; onStreamEnd?: () => void } {
  return typeof h === 'object' && h !== null && 'onTranscript' in h;
}

// helper to emit to UI
function emitTranscript(type: 'partial' | 'final', text: string) {
  const h = _transcriptHandler;
  if (!h) return;
  try {
    const payload: TranscriptEvent = { type, text };
    if (isObjectHandler(h)) h.onTranscript(payload);
    else h(payload);
  } catch {
    // swallow
  }
}

// helper to notify unexpected stream end (only works if object-form handler is provided)
function emitStreamEnd() {
  const h = _transcriptHandler;
  if (h && isObjectHandler(h)) {
    try { h.onStreamEnd?.(); } catch { /* swallow */ }
  }
}

/** Dev kill switch (defaults to TRUE for users). Example:
 *   localStorage.setItem('cb_flag_use_worklet','false')
 */
function readFlagDefaultTrue(key: string): boolean {
  try {
    const v = localStorage.getItem(key);
    // missing => true; else parse bool
    return v === null ? true : v.toLowerCase() === 'true';
  } catch {
    return true;
  }
}

let ws: WebSocket | null = null;
let audioCtx: AudioContext | null = null;
let sourceNode: MediaStreamAudioSourceNode | null = null;
let procNode: ScriptProcessorNode | null = null;
let workletNode: AudioWorkletNode | null = null;
let hbTimer: number | null = null;
let mediaStream: MediaStream | null = null;

let sentFrames = 0;
let sentBytes = 0;

// Track "have we already notified onStreamEnd for this socket?"
const endNotified = new WeakMap<WebSocket, boolean>();
function notifyStreamEndOnce(sock: WebSocket) {
  if (!endNotified.get(sock)) {
    endNotified.set(sock, true);
    emitStreamEnd();
  }
}

// PATCH: make heartbeat idempotent and never accumulate multiple intervals
function startHeartbeat(sock: WebSocket) {
  stopHeartbeat(); // <-- ensure any prior heartbeat is cleared
  hbTimer = window.setInterval(() => {
    if (sock.readyState === WebSocket.OPEN) {
      try {
        sock.send(JSON.stringify({ type: 'ping', t: Date.now() }));
        console.log('[WS->] ping');
      } catch {
        // ignore send errors; onclose/onerror will handle cleanup
      }
    }
  }, 10000);
}

function stopHeartbeat() {
  if (hbTimer) {
    clearInterval(hbTimer);
    hbTimer = null;
  }
}

function floatTo16PCM(float32: Float32Array): ArrayBuffer {
  const len = float32.length;
  const buf = new ArrayBuffer(len * 2);
  const view = new DataView(buf);
  let offset = 0;
  for (let i = 0; i < len; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, float32[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
  }
  return buf;
}

// naive downsampler from 48k to 16k (factor 3). Good enough for MVP.
function downsampleTo16k(input: Float32Array, inSampleRate: number): Float32Array {
  if (inSampleRate === 16000) return input;
  const ratio = inSampleRate / 16000;
  const outLen = Math.floor(input.length / ratio);
  const out = new Float32Array(outLen);
  let i = 0, j = 0;
  while (i < outLen) {
    const nextJ = Math.floor((i + 1) * ratio);
    let sum = 0;
    let count = 0;
    for (; j < nextJ && j < input.length; j++) {
      sum += input[j];
      count++;
    }
    out[i++] = count > 0 ? sum / count : 0;
  }
  return out;
}

/** Try to register a tiny inline AudioWorklet that forwards mono float32 blocks to main.
 *  We still downsample + PCM encode on main, keeping server protocol untouched.
 */
async function tryInitWorklet(ctx: AudioContext): Promise<boolean> {
  // Respect dev kill-switch; default ON for everyone.
  if (!readFlagDefaultTrue('cb_flag_use_worklet')) return false;
  // Feature detection
  if (!('audioWorklet' in ctx)) return false;

  try {
    const code = `
      class CBProcessor extends AudioWorkletProcessor {
        process (inputs) {
          const input = inputs[0];
          if (input && input[0] && input[0].length) {
            // Copy channel 0 to a transferable buffer
            const ch0 = input[0];
            const out = new Float32Array(ch0.length);
            out.set(ch0);
            this.port.postMessage(out, [out.buffer]);
          }
          return true;
        }
      }
      registerProcessor('cueboard-proc', CBProcessor);
    `;
    const blob = new Blob([code], { type: 'application/javascript' });
    const url = URL.createObjectURL(blob);
    await ctx.audioWorklet.addModule(url);
    URL.revokeObjectURL(url);
    return true;
  } catch (e) {
    console.warn('[AUDIO] Worklet unavailable, falling back to ScriptProcessor.', e);
    return false;
  }
}

// --- Private ---

async function startStreaming(stream: MediaStream) {
  mediaStream = stream; // store for cleanup
  audioCtx = new ((window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext)({ sampleRate: 48000 });
  sourceNode = audioCtx.createMediaStreamSource(stream);

  const inSampleRate = audioCtx.sampleRate || 48000;
  console.log('[AUDIO] ctx sampleRate', inSampleRate, 'channelCount', sourceNode.channelCount);

  // Prefer AudioWorklet; fallback to ScriptProcessor if it fails.
  const useWorklet = await tryInitWorklet(audioCtx);

  if (useWorklet) {
    workletNode = new AudioWorkletNode(audioCtx, 'cueboard-proc', {
      numberOfInputs: 1,
      numberOfOutputs: 0,
      channelCount: 1,
    });
    workletNode.port.onmessage = (ev: MessageEvent) => {
      // Use the GLOBAL ws to reflect current connection
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      const input = ev.data as Float32Array;
      const ds = downsampleTo16k(input, inSampleRate);
      const buf = floatTo16PCM(ds);
      sentFrames++;
      sentBytes += (buf.byteLength || 0);
      try { ws.send(buf); } catch {}
    };

    // Build graph: source -> worklet (no outputs)
    sourceNode.connect(workletNode);
    console.log('[AUDIO] worklet path enabled');
  } else {
    procNode = audioCtx.createScriptProcessor(4096, sourceNode.channelCount, 1);
    procNode.onaudioprocess = (e) => {
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      const input = e.inputBuffer.getChannelData(0); // downmix to mono
      const ds = downsampleTo16k(input, inSampleRate);
      const buf = floatTo16PCM(ds);
      sentFrames++;
      sentBytes += (buf.byteLength || 0);
      try { ws.send(buf); } catch {}
    };
    sourceNode.connect(procNode);
    procNode.connect(audioCtx.destination);
    console.log('[AUDIO] script-processor path enabled');
  }

  console.log('[AUDIO] streaming started');
}

function wireSocketHandlers(sock: WebSocket) {
  // Message / close / error only; onopen is set by the caller for each mode (fresh/soft)
  sock.onmessage = (ev) => {
    try {
      const raw = typeof ev.data === 'string' ? ev.data : '';
      const msg = raw ? JSON.parse(raw) : null;
      if (msg?.type === 'partial') {
        emitTranscript('partial', msg.text || '');
      } else if (msg?.type === 'final') {
        emitTranscript('final', msg.text || '');
      } else if (msg?.type === 'transcript') {
        // tolerate servers that send {type:'transcript', text, final}
        emitTranscript(msg.final ? 'final' : 'partial', msg.text || '');
      } else if (msg?.type === 'error') {
        console.error('[WS<-] error', msg.message);

        // Treat terminal errors as a hard end — stop heartbeat, close socket, notify UI (deduped).
        const m = String(msg.message || '').toLowerCase();
        const isDurationMax =
          m.includes('maximum allowed stream duration') ||
          m.includes('exceeded maximum allowed stream duration');
        const isDestroyed =
          m.includes('stream was destroyed') ||
          m.includes('cannot call write after a stream was destroyed');

        if (isDurationMax || isDestroyed) {
          try { stopHeartbeat(); } catch {}
          try {
            if (sock.readyState === WebSocket.OPEN || sock.readyState === WebSocket.CONNECTING) {
              // Close with explicit non-1000 code to distinguish from user stop.
              sock.close(4001, isDurationMax ? 'expired' : 'destroyed');
            }
          } catch {}
          // Notify once per socket (dedup with onclose)
          try { notifyStreamEndOnce(sock); } catch {}
        }
      }
    } catch {
      // ignore malformed
    }
  };

  sock.onclose = (ev) => {
    stopHeartbeat();
    console.log('[WS] close', { code: ev.code, reason: ev.reason, wasClean: ev.wasClean });
    // Notify UI only for unexpected closures (not user/soft stop 1000)
    if (!ev.wasClean || ev.code !== 1000) {
      notifyStreamEndOnce(sock);
    }
  };

  sock.onerror = (ev) => {
    console.error('[WS] error', ev);
    // classification handled in onclose
  };
}

// --- Public API ---

async function openConnectionAndStart(streamFn: () => Promise<MediaStream>) {
  // Avoid duplicate connects while OPEN or CONNECTING
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
    console.log('[WS] already open/connecting');
    return;
  }

  const sock = new WebSocket(getWsUrl());
  ws = sock;
  wireSocketHandlers(sock);

  sock.onopen = (async () => {
    console.log('[WS] open', sock.url);

    const cfg = {
      config: {
        sampleRateHertz: 16000,
        languageCode: 'en-US',
        enableAutomaticPunctuation: true,
      },
    };
    try { sock.send(JSON.stringify(cfg)); console.log('[WS->] config', cfg); } catch {}

    const startMsg = { type: 'start', format: 's16le', sampleRate: 16000, channels: 1 };
    try { sock.send(JSON.stringify(startMsg)); console.log('[WS->] start', startMsg); } catch {}

    startHeartbeat(sock);

    try {
      const stream = await streamFn();
      await startStreaming(stream);
    } catch (err) {
      console.error('[AUDIO] failed to get stream', err);
    }
  });
}

/**
 * Soft socket-only restart that reuses the existing MediaStream capture.
 * - No getDisplayMedia() call (no user gesture required).
 * - Keeps audioCtx/sourceNode/procNode or workletNode and media tracks alive.
 * - Rebinds global ws so the audio processing loop streams to the new socket.
 */
export async function softRestartStreaming(): Promise<void> {
  // If no capture, there's nothing to reuse. Surface this so UI can show "Re-share".
  if (!mediaStream) {
    throw new Error('no-capture');
  }

  // Gracefully close the old socket (clean close so UI doesn't get a duplicate onStreamEnd)
  try { stopHeartbeat(); } catch {}
  if (ws) {
    try {
      if (ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ type: 'stop' })); } catch {}
      }
      ws.close(1000, 'wd-refresh'); // clean close → onclose won't emit onStreamEnd
    } catch {}
  }
  ws = null;

  // Open a fresh socket and wire handlers
  await new Promise<void>((resolve) => {
    const sock = new WebSocket(getWsUrl());
    ws = sock;
    wireSocketHandlers(sock);

    sock.onopen = async () => {
      console.log('[WS] open', sock.url);

      const cfg = {
        config: {
          sampleRateHertz: 16000,
          languageCode: 'en-US',
          enableAutomaticPunctuation: true,
        },
      };
      try { sock.send(JSON.stringify(cfg)); console.log('[WS->] config', cfg); } catch {}

      const startMsg = { type: 'start', format: 's16le', sampleRate: 16000, channels: 1 };
      try { sock.send(JSON.stringify(startMsg)); console.log('[WS->] start', startMsg); } catch {}

      startHeartbeat(sock);

      // If graph was torn down for some reason but capture exists, rebuild just the graph.
      if (mediaStream && !procNode && !workletNode) {
        try { await startStreaming(mediaStream); } catch (e) { console.error('[AUDIO] graph rebuild failed', e); }
      }

      resolve();
    };
  });
}

export async function startMicCapture() {
  await openConnectionAndStart(() => navigator.mediaDevices.getUserMedia({ audio: true }));
}

export async function startSystemCapture() {
  await openConnectionAndStart(async () => {
    const stream = await navigator.mediaDevices.getDisplayMedia({
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        channelCount: 2,
      },
      video: true,
    });
    // Immediately stop the video track, we only want audio
    stream.getVideoTracks().forEach(track => track.stop());
    return stream;
  });
}

/**
 * Stop streaming.
 * - Default: full teardown (tracks, audio graph, socket).
 * - With {preserveCapture:true}: keep MediaStream + audio graph alive; close socket only.
 */
export async function stopStreaming(opts?: { preserveCapture?: boolean }) {
  const preserve = !!opts?.preserveCapture;

  // Always stop heartbeat
  stopHeartbeat();

  if (!preserve) {
    if (mediaStream) {
      try { mediaStream.getTracks().forEach(track => track.stop()); } catch {}
      mediaStream = null;
    }

    try {
      if (procNode) {
        procNode.disconnect();
        procNode.onaudioprocess = null;
        procNode = null;
      }
      if (workletNode) {
        try { workletNode.port.onmessage = null as any; } catch {}
        try { workletNode.disconnect(); } catch {}
        workletNode = null;
      }
      if (sourceNode) {
        sourceNode.disconnect();
        sourceNode = null;
      }
      if (audioCtx) {
        await audioCtx.close();
        audioCtx = null;
      }
    } catch {
      // ignore
    }
  }

  if (ws) {
    try {
      if (ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify({ type: 'stop' })); } catch {}
        console.log('[WS->] STOP');
      }
      // Clean close: UI treats 1000 as expected (no onStreamEnd)
      ws.close(1000, preserve ? 'soft stop' : 'user stop');
    } catch {}
  }
  ws = null;

  if (!preserve) {
    sentFrames = 0;
    sentBytes = 0;
  }
}
